import { getEnvironmentInfo } from "./config";
import {
  API_EXCEPTION_STATUS_CODE,
  API_FORBIDDEN_403_DEFAULT_ERR_MSG_ADMIN,
  API_FORBIDDEN_STATUS_CODE,
  API_SESSION_EXPIRE_STATUS_CODE,
  DEFAULT_ERR_MSG_ADMIN
} from "./constants";
import { AppType } from "./enums";
import get from "lodash/get";
import * as Sentry from "@sentry/browser";
import React from "react";
import { getDefaultSupportEmail } from ".";
import { Copies } from "shared/hooks/useCopies";
import { AxiosError } from "axios";
import { errorToast } from "./toastNotification";

export function isNetWorkError(err: any) {
  return err?.message?.toLowerCase?.() === "network error";
}

export function getErrorMessageForFileUpload(err: any, copies: Copies) {
  if (isNetWorkError(err)) {
    errorToast(copies.get("file.upload.network.error"));
  } else {
    errorToast(getErrorMessage(err));
  }
}

export function getErrorMessage(err: any, defaultMsg?: string) {
  if (err?.message?.toLowerCase?.() === "network error") {
    return err.message;
  } else if (err?.response?.status === API_SESSION_EXPIRE_STATUS_CODE) {
    return null;
  } else if (err?.response?.status === API_EXCEPTION_STATUS_CODE) {
    if (err?.request?.responseType === "blob" && err?.response?.data instanceof Blob) {
      showErrorMessageForBlob(err);
    } else {
      return err?.response?.data?.message;
    }
  } else if (err?.response?.status === API_FORBIDDEN_STATUS_CODE && getEnvironmentInfo().appType === AppType.CONSOLE) {
    return API_FORBIDDEN_403_DEFAULT_ERR_MSG_ADMIN;
  } else if (defaultMsg) {
    return defaultMsg;
  } else {
    /**
     * @todo : RR revisit this block
     */
    let id = "";
    let url = "";
    try {
      id = get(err, "response.headers.correlationid", "");
      url = get(err, "response.config.url", "");

      if (url || id) {
        // Api logs are already available in GCP logs / mails and we don't need to duplicate them in Sentry
        //  this is an example of how to use Sentry for BE related errors
        // const Url = new URL(url);
        // const params = get(err, "response.config.params", {});
        // const api = Url.pathname || url;
        // Sentry.withScope((scope: any) => {
        //   scope.setTags({
        //     crId: id,
        //     api
        //   });
        //   scope.setExtras({
        //     ...params
        //   });
        //   Sentry.captureException(new Error(`Api - BE`));
        // });
      } else {
        Sentry.withScope((scope: any) => {
          scope.setExtras({
            err: JSON.stringify(err)
          });
          Sentry.captureException(new Error("FE"));
        });
      }
    } catch (e) {}
    return getEnvironmentInfo().appType === AppType.CONSOLE ? (
      DEFAULT_ERR_MSG_ADMIN
    ) : (
      <React.Fragment>
        We are unable to process your request at this time. Please email&nbsp;<b>{getDefaultSupportEmail()}</b>&nbsp;for
        assistance.
      </React.Fragment>
    );
  }
}

export function getErrorCode(err: any) {
  return err?.response?.data?.message;
}

export function shouldRefreshPageOnError(err: any) {
  return err?.response?.data?.refreshPage;
}

export function shouldDisplayFullPageError(err: any) {
  return err?.response?.data?.displayFullPage;
}

export function getHttpStatusCode(err: any) {
  return err?.response?.status;
}

export function getSessionExpiredMsg(copies: Copies) {
  switch (getEnvironmentInfo().appType) {
    case AppType.CONSOLE:
      return <React.Fragment>{copies.get("session.expired")}</React.Fragment>;
    case AppType.REGISTRATION:
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: copies.get("resume.link.expired", {
              0: `<b>${getDefaultSupportEmail()}</b>`
            })
          }}
        />
      );
    case AppType.MYEXPERIENCE:
    default:
      const defaultSupportEmail = getDefaultSupportEmail();
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: copies.get(defaultSupportEmail ? "session.expired.with.support.email" : "session.expired", {
              0: `<b>${getDefaultSupportEmail()}</b>`
            })
          }}
        />
      );
  }
}

/**
 * In case of responseType blob response object is promise.
 * This function gets error message and shows it in error toast
 */
export async function showErrorMessageForBlob(err: AxiosError<any>) {
  try {
    if (err?.response?.status === API_EXCEPTION_STATUS_CODE) {
      let responseObj = JSON.parse(await err?.response?.data?.text());
      errorToast(responseObj.message);
    }
  } catch (err) {}
}
